/*
 * Swiper wird dynamisch geladen, damit Webpack die Bibliothek in einen
 * gemeinsamen Chunk legen kann. Zuvor bundelte jeder Slider-Block seine eigene
 * Kopie — auf der Startseite lud der Browser dieselben rund 89 KB viermal.
 *
 * Das top-level await haelt die Signaturen unten synchron: Das Modul wird erst
 * ausgewertet, wenn der Chunk da ist, und die Blocks laden dieses Modul ohnehin
 * bereits per import(). Es setzt Chrome 89, Safari 15 oder Firefox 89 voraus —
 * dieselbe Generation, die auch die uebrigen ES-Module hier braucht.
 */
const { default: Swiper } = await import('swiper')

// Initialize global swipers array if it doesn't exist
window.swipers = window.swipers || []

/**
 * Generates a unique ID for Swiper groups
 * Useful when multiple instances of the same block exist
 *
 * @param {HTMLElement} element - Container element for the Swiper
 * @param {string} prefix - Optional prefix for the ID ('gallery', 'product', etc.)
 * @returns {string} - Unique ID
 *
 * @example
 * const groupId = generateSwiperGroupId(blockElement, 'gallery');
 * // Returns: 'gallery-1710166354321-487'
 */
export function generateSwiperGroupId(element, prefix = '') {
  if (element.dataset.swiperGroupId) {
    return element.dataset.swiperGroupId
  }

  const uniqueId = `${prefix ? prefix + '-' : ''}${Date.now()}-${Math.floor(Math.random() * 1000)}`
  element.dataset.swiperGroupId = uniqueId

  return uniqueId
}

/**
 * Initializes a Swiper slider and adds it to window.swipers
 *
 * @param {string} name - Slider name
 * @param {HTMLElement} ref - DOM element reference
 * @param {Object} config - Swiper configuration
 * @param {string|null} group - Optional group name for related sliders
 * @returns {Swiper|null} - Initialized Swiper instance or null
 *
 * @example
 * // Single slider
 * const slider = initialiseSwiperSlider('products', sliderElement, sliderConfig);
 *
 * // Grouped sliders (main + thumbs)
 * const thumbs = initialiseSwiperSlider('thumbs', thumbsElement, thumbsConfig, groupId);
 * const main = initialiseSwiperSlider('main', mainElement, mainConfig, groupId);
 */
export function initialiseSwiperSlider(name, ref, config, group = null) {
  if (!ref || !config) return null

  const swiper = new Swiper(ref, config)

  if (group) {
    const existingGroupIndex = window.swipers.findIndex(s => s.group === group)

    if (existingGroupIndex >= 0) {
      window.swipers[existingGroupIndex].instances[name] = swiper
    } else {
      window.swipers.push({
        group: group,
        instances: { [name]: swiper },
      })
    }
  } else {
    window.swipers.push({
      name: name,
      instance: swiper,
    })
  }

  return swiper
}

/**
 * Gets a Swiper instance by name
 *
 * @param {string} name - Slider name to retrieve
 * @param {string|null} group - Optional group name if slider is part of a group
 * @returns {Swiper|null} - Swiper instance or null if not found
 *
 * @example
 * // Get individual slider
 * const slider = getSwiperByName('products');
 *
 * // Get slider from a group
 * const mainSlider = getSwiperByName('main', groupId);
 */
export function getSwiperByName(name, group = null) {
  if (group) {
    const groupObj = window.swipers.find(s => s.group === group)
    return groupObj ? groupObj.instances[name] || null : null
  } else {
    const swiperObj = window.swipers.find(s => s.name === name)
    return swiperObj ? swiperObj.instance : null
  }
}

/**
 * Gets all Swiper instances in a group
 *
 * @param {string} group - Group name
 * @returns {Object|null} - Object with all instances in the group or null
 *
 * @example
 * const sliders = getSwipersInGroup(groupId);
 * // Returns: { main: Swiper{}, thumbs: Swiper{} }
 */
export function getSwipersInGroup(group) {
  const groupObj = window.swipers.find(s => s.group === group)
  return groupObj ? groupObj.instances : null
}
